import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import { applyColumnOrder, moveColumnInOrder, normalizeColumnOrder } from '../../utils/columnLayout';

const STORAGE_KEY = 'materialIndexVisibleColumns';

const MaterialIndexColumnConfigModal = ({ isOpen, onClose, columns, visibleColumns, onSave, onMappingsChange, columnOrder }) => {
  const { instance, accounts } = useMsal();
  const [localVisibleColumns, setLocalVisibleColumns] = useState(new Set());
  const [localOrder, setLocalOrder] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('visibility'); // 'visibility' or 'udf'

  useEffect(() => {
    if (isOpen) {
      setLocalVisibleColumns(new Set(visibleColumns));
      setLocalOrder(normalizeColumnOrder(columnOrder || columns.map(c => c.key), columns.map(c => c.key)));
      setError('');
      // Load current mappings
      loadMappings();
    }
  }, [isOpen, visibleColumns]);

  const loadMappings = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/ottaviano/column-mappings');
      if (response.data.success) {
        setMappings(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading column mappings:', err);
    }
  };

  const handleToggleColumn = (columnKey) => {
    setLocalVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        if (newSet.size > 1) {
          newSet.delete(columnKey);
        }
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allKeys = [...columns.map(c => c.key)];
    // Add UDF columns that have mappings
    mappings.forEach(m => {
      if (!allKeys.includes(m.column_key)) {
        allKeys.push(m.column_key);
      }
    });
    setLocalVisibleColumns(new Set(allKeys));
  };

  const handleSelectNone = () => {
    setLocalVisibleColumns(new Set([columns[0].key]));
  };

  // UDF Column Management
  const handleAddMapping = () => {
    const usedColumns = new Set(mappings.map(m => m.column_key));
    for (let i = 1; i <= 10; i++) {
      const columnKey = `udf${String(i).padStart(2, '0')}`;
      if (!usedColumns.has(columnKey)) {
        setMappings([...mappings, {
          column_key: columnKey,
          custom_name: '',
          is_editable: true,
          original_excel_header: ''
        }]);
        break;
      }
    }
  };

  const handleRemoveMapping = (index) => {
    const newMappings = [...mappings];
    const removedKey = newMappings[index].column_key;
    newMappings.splice(index, 1);
    setMappings(newMappings);
    // Also remove from visible columns
    setLocalVisibleColumns(prev => {
      const newSet = new Set(prev);
      newSet.delete(removedKey);
      return newSet;
    });
  };

  const handleMappingChange = (index, field, value) => {
    const newMappings = [...mappings];
    newMappings[index][field] = value;
    setMappings(newMappings);
  };

  const handleSave = async () => {
    setError('');
    
    // Validate mappings
    const customNames = mappings.map(m => m.custom_name.trim()).filter(n => n);
    const uniqueNames = new Set(customNames);
    
    if (customNames.length !== uniqueNames.size) {
      setError('Custom column names must be unique');
      return;
    }
    
    for (const mapping of mappings) {
      if (!mapping.custom_name || !mapping.custom_name.trim()) {
        setError('All mapped columns must have a custom name');
        return;
      }
    }
    
    try {
      setSaving(true);
      
      // Save column mappings to backend
      if (mappings.length > 0) {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          '/ottaviano/column-mappings',
          { mappings }
        );
        
        if (!response.data.success) {
          setError(response.data.message || 'Failed to save mappings');
          return;
        }
        
        // Notify parent of mapping changes
        if (onMappingsChange) {
          onMappingsChange(response.data.data);
        }
      } else {
        // Clear all mappings
        await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          '/ottaviano/column-mappings',
          { mappings: [] }
        );
        if (onMappingsChange) {
          onMappingsChange([]);
        }
      }
      
      // Save visibility preferences
      onSave(localVisibleColumns, localOrder);
      onClose();
    } catch (err) {
      console.error('Error saving configuration:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const usedColumns = new Set(mappings.map(m => m.column_key));
  const canAddMore = usedColumns.size < 10;
  
  // Get all displayable columns (base columns + mapped UDF columns)
  const allDisplayableColumns = [...columns];
  mappings.forEach(m => {
    if (!allDisplayableColumns.find(c => c.key === m.column_key)) {
      allDisplayableColumns.push({
        key: m.column_key,
        label: m.custom_name || m.column_key,
        isUdf: true
      });
    }
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            ⚙️ Column Configuration
          </h3>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: saving ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' }}>
          <button
            onClick={() => setActiveTab('visibility')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderBottom: activeTab === 'visibility' ? '2px solid #007bff' : '2px solid transparent',
              marginBottom: '-2px',
              backgroundColor: activeTab === 'visibility' ? '#f8f9fa' : 'transparent',
              color: activeTab === 'visibility' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'visibility' ? 'bold' : 'normal',
              fontSize: '0.95rem'
            }}
          >
            👁️ Column Visibility
          </button>
          <button
            onClick={() => setActiveTab('udf')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderBottom: activeTab === 'udf' ? '2px solid #007bff' : '2px solid transparent',
              marginBottom: '-2px',
              backgroundColor: activeTab === 'udf' ? '#f8f9fa' : 'transparent',
              color: activeTab === 'udf' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'udf' ? 'bold' : 'normal',
              fontSize: '0.95rem'
            }}
          >
            ➕ Custom Columns ({mappings.length}/10)
          </button>
        </div>

        {/* Visibility Tab */}
        {activeTab === 'visibility' && (
          <>
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              color: '#1976d2'
            }}>
              <strong>Note:</strong> Show, hide, and rearrange columns. Your layout is saved on this browser.
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={handleSelectAll} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>Select All</button>
              <button onClick={handleSelectNone} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>Select None</button>
              <span style={{ marginLeft: 'auto', color: '#666', fontSize: '0.85rem' }}>
                {localVisibleColumns.size} of {allDisplayableColumns.length} columns visible
              </span>
            </div>

            <ColumnConfigPanel
              columns={applyColumnOrder(allDisplayableColumns, localOrder)}
              isVisible={(key) => localVisibleColumns.has(key)}
              onToggle={handleToggleColumn}
              onMove={(from, to) => setLocalOrder(prev => moveColumnInOrder(normalizeColumnOrder(prev, allDisplayableColumns.map(c => c.key)), from, to))}
              compact={false}
              getLabel={(col) => `${col.label}${col.isUdf ? ' (Custom)' : ''}`}
            />
          </>
        )}

        {/* UDF Columns Tab */}
        {activeTab === 'udf' && (
          <>
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#1976d2'
            }}>
              <strong>Note:</strong> Add up to 10 custom columns to the Material Index. 
              Each column is stored in the database and can be used for any purpose.
            </div>

            {mappings.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  No custom columns configured yet.
                </p>
                <button
                  onClick={handleAddMapping}
                  disabled={!canAddMore}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  + Add Custom Column
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  {mappings.map((mapping, index) => (
                    <div key={index} style={{
                      padding: '1rem',
                      marginBottom: '1rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa'
                    }}>
                      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#666' }}>
                            Database Column:
                          </label>
                          <select
                            value={mapping.column_key}
                            onChange={(e) => handleMappingChange(index, 'column_key', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '0.9rem'
                            }}
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => {
                              const udfKey = `udf${String(num).padStart(2, '0')}`;
                              const isUsedByOther = usedColumns.has(udfKey) && mapping.column_key !== udfKey;
                              return (
                                <option key={udfKey} value={udfKey} disabled={isUsedByOther}>
                                  UDF{String(num).padStart(2, '0')} {isUsedByOther ? '(already used)' : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        <div style={{ flex: 2, minWidth: '200px' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#666' }}>
                            Display Name: *
                          </label>
                          <input
                            type="text"
                            value={mapping.custom_name}
                            onChange={(e) => handleMappingChange(index, 'custom_name', e.target.value)}
                            placeholder="Enter column name..."
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}>
                          <input
                            type="checkbox"
                            checked={mapping.is_editable}
                            onChange={(e) => handleMappingChange(index, 'is_editable', e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          Editable in Table
                        </label>

                        <button
                          onClick={() => handleRemoveMapping(index)}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {canAddMore && (
                  <button
                    onClick={handleAddMapping}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      marginBottom: '1rem'
                    }}
                  >
                    + Add Another Column
                  </button>
                )}
              </>
            )}
          </>
        )}

        {/* Footer Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          marginTop: '1.5rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid #e0e0e0'
        }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: saving ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: saving ? 0.5 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper functions for localStorage
export const loadVisibleColumns = (allColumnKeys) => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const validColumns = parsed.filter(key => allColumnKeys.includes(key));
      if (validColumns.length > 0) {
        return new Set(validColumns);
      }
    }
  } catch (e) {
    console.error('Error loading column config:', e);
  }
  return new Set(allColumnKeys);
};

export const saveVisibleColumns = (visibleColumns) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...visibleColumns]));
  } catch (e) {
    console.error('Error saving column config:', e);
  }
};

export default MaterialIndexColumnConfigModal;
